import utils from "./Utils.js";
import plotMgrInstance from "./PlotMgr.js"
import * as d3 from "d3";

class Plot {
  constructor(initParams) {
    let _defaultParams = plotMgrInstance.parameters;
    let params = { ..._defaultParams, ...(initParams || {}) }

    this.canvas = null;
    this.name = null;

    this.digits = params.digits;
    this.margin = params.margin;
    this.backgroundColor = params.backgroundColor;

    this.axisColor = params.axisColor;
    this.axisAngle = params.axisAngle;
    
    this.gridColor = params.gridColor;
    this.gridOpacity = params.gridOpacity;

    this.labelColor = params.labelColor;
    this.labelSize = params.labelSize;
    this.labelAligned = params.labelAligned;

    this.defaultColor = params.defaultColor;
    this.defaultOpacity = params.defaultOpacity;
    this.defaultWidth = params.defaultWidth;
    this.defaultRadius = params.defaultRadius;

    this.selectionColor = params.selectionColor;
    this.selectionOpacity = params.selectionOpacity;
    this.selectionWidth = params.selectionWidth;

    this.highlightColor = params.highlightColor;
    this.highlightOpacity = params.highlightOpacity;
    this.highlightWidth = params.highlightWidth;

    this.multipleSelectionFlagCheck = null;
    this.highlightElementFlagCheck = null;

    this.selectedElements = [];
    this.temp_selection = [];
    this.temp_highlight = [];
    this.temp_color = [];

    this.svg = null;
    this.tooltip = null;
    this.plotKey = "";
    this.data = {};
    this.keys = {};
    this.elements = {};
    this.groups = {};
    this.plotAdded = false
  }

  addViewer(canvas, name) {
    let scope = this;

    scope.canvas = canvas;
    scope.name = name;
    d3.select(scope.canvas)
      .attr('width', scope.canvas.offsetWidth)
      .attr('height', scope.canvas.offsetHeight)
      .style('background', scope.backgroundColor)
      .style('display', 'inline-block');

    // Create a ResizeObserver to watch the canvas parent element
    let resizeObserver = new ResizeObserver(() => {
      if (!scope.plotAdded) return 
      scope.resizeChart();
    });

    // Observe the canvas parent element
    resizeObserver.observe(scope.canvas.parentElement);
  }

  // cleanup the instance
  destroy() {
    let scope = this;
    scope.canvas.innerHTML = '';
  }

  //  ████  ███  █   █ █████ █████  ████ █   █ ████  █████ 
  // █     █   █ ██  █ █       █   █     █   █ █   █ █     
  // █     █   █ █ █ █ ████    █   █  ██ █   █ ████  ████  
  // █     █   █ █  ██ █       █   █   █ █   █ █   █ █     
  //  ████  ███  █   █ █     █████  ███  █████ █   █ █████ 

  // callback : register event 
  registerEvent(eventName, callBack) {
    let scope = this;

    switch (eventName) {
      case 'element-select':
        d3.select(scope.canvas).on('click', (e) => scope.pickElement(e, callBack), false);
        break;

      case 'element-highlight':
        d3.select(scope.canvas).on('mousemove', (e) => scope.highlightElement(e, callBack), false);
        break;

      case 'scrub-selection':
        scope.onScrub = callBack;
        break;
    }
  }

  // multiple selection check
  multipleSelectionCheck(status) {
    let scope = this;

    scope.multipleSelectionFlagCheck = status;
  }

  // highlight element flag check 
  highlightCheck(status) {
    let scope = this;

    scope.highlightElementFlagCheck = status;
  }

  //  ████  ███  █      ███  ████  ████  █   █ 
  // █     █   █ █     █   █ █   █ █   █  █ █  
  // █     █   █ █     █   █ ████  ████    █   
  // █     █   █ █     █   █ █   █ █   █   █   
  //  ████  ███  █████  ███  █   █ ████    █   
  
  colorElements(attribute, legendDictionary) {
    let scope = this;

    if (scope.plotKey == attribute) {
      Object.keys(scope.groups).forEach(key => {
        let highlightedElement = scope.getHighlightedElement(scope.groups[key]);
        scope.applyColor(highlightedElement, legendDictionary[key])
      });
    }
    else {
      scope.clearAllColorBy();
    }
  }

  // apply color by color
  applyColor(element, color) {
    let scope = this;

    scope.temp_color.push(element);

    if (scope.type == "ParallelCoordinatePlot"){
      if (!((element.attr('data-isSel') == 'true') || (element.attr('data-isPar') == 'true'))) {
        element.style("stroke", color);
        element.style("fill", "none");
        element.style('opacity', scope.baseOpacity);
      }
    }

    else {
      if (!((element.attr('data-isSel') == 'true') || (element.attr('data-isPar') == 'true'))) {
        element.style("fill", color);
        element.style('opacity', 1.0);
      }
    }

    element.attr('data-color', color);
    element.attr('data-isClr', true);
  }

  // Clear all color by
  clearAllColorBy() {
    let scope = this;

    for (let i = 0; i < scope.temp_color.length; i++) {
      let element = scope.temp_color[i];
      element.attr('data-isClr', false);

      var color = scope.defaultColor;
      var opacity = 1.0;
      if (element.attr('data-isSel') == 'true') var color = scope.selectionColor;
      if (element.attr('data-isPar') == 'true') {
        color = scope.selectionColor;
        opacity = scope.selectionOpacity;
      }

        if (scope.type == "ParallelCoordinatePlot"){
          element.style("stroke", color);
          element.style("fill", "none");
          element.style('opacity', opacity);
        }

        else {
          element.style("fill", color);
          element.style('opacity', opacity);
        }
      }
    scope.temp_color = [];
  }

  // █████ █████ █     █████  ████ █████ 
  // █     █     █     █     █       █   
  // █████ ████  █     ████  █       █   
  //     █ █     █     █     █       █   
  // █████ █████ █████ █████  ████   █   

  // SELECTION event
  pickElement(event, callBack) {
    let scope = this;

    scope.clearSelection();

    let group = event.target;
    let selectedElement;
    if (selectedElement = d3.select(group)) {
      scope.getSelectedElementsCondition(group, selectedElement, callBack);
    }
  }

  getSelectedElementIds(group, selectedElement){
    return group.__data__.ids ? group.__data__.ids : group.__data__.data.ids
  }

  getSelectedElementsCondition(group, selectedElement, callBack, ids){
    let scope = this;
    if (group.__data__ != undefined) {
      let ids = scope.getSelectedElementIds(group, selectedElement)
      ids.forEach(id => {
        scope.selectedElements.push(id)
      });
      scope.applySelectionColor(selectedElement);
      callBack([...new Set(scope.selectedElements)]);
    }
    else {
      callBack("");
      plotMgrInstance.deSelectElementsInAllPlots();
    }
  }

  // apply selection color
  applySelectionColor(element) {
    let scope = this;

    if (scope.type == "ParallelCoordinatePlot") {
      scope.temp_selection.push(element);
      element.style("stroke", scope.selectionColor);
      element.style("fill", "none");
      element.style("stroke-width", scope.highlightWidth);
      element.style('opacity', scope.baseOpacity);
      element.attr('data-isSel', true);
    }

    else if (scope.type == "PiePlot") {
      scope.temp_pick.push(element)
      element.style("fill", scope.selectionColor)
      element.attr('name', 'pickElement');
    }

    else {
      scope.temp_selection.push(element);
      element.style("fill", scope.selectionColor);
      element.style('opacity', 1.0);
      element.attr('data-isSel', true);

    }
  }

  // apply partial selection
  applyPartialSelectionColor(element) {
    let scope = this;

    if (scope.type == "ParallelCoordinatePlot"){
      scope.temp_selection.push(element)
      element.style('stroke', scope.selectionColor);
      element.style("stroke-width", scope.defaultWidth);
      element.style('opacity', scope.selectionOpacity);
      element.attr('data-isPar', true);
      element.style("fill", "none");
    }

    else {
      scope.temp_selection.push(element)
      element.style('fill', scope.selectionColor);
      element.style('opacity', scope.selectionOpacity);
      element.attr('data-isPar', true);
    }
  }

  // Clear pick selection
  clearSelection() {
    let scope = this;

    if (!scope.multipleSelectionFlagCheck) {
      for (let i = 0; i < scope.temp_selection.length; i++) {
        let element = scope.temp_selection[i];

        element.attr('data-isSel', false);
        element.attr('data-isPar', false);

        var color = scope.defaultColor ;
        if (element.attr('data-isClr') == 'true') color = element.attr('data-color');

        if (scope.type == "ParallelCoordinatePlot"){
          element.style("fill", "none");
          element.style("stroke", color);
          element.style("stroke-width", scope.defaultWidth);
          element.style('opacity', scope.defaultOpacity);
        }
        else {
          element.style("fill", color);
          element.style('opacity', 1.0);
        }
      }
      scope.temp_selection = [];
      scope.selectedElements = [];
    }
  }

  // Clear all pick selection
  clearAllPickSelection() {
    let scope = this;

    for (let i = 0; i < scope.temp_selection.length; i++) {
      let element = scope.temp_selection[i];

      element.attr('data-isSel', false);
      element.attr('data-isPar', false);

      var color = scope.defaultColor;
      if (element.attr('data-isClr') == 'true') color = element.attr('data-color');

      if (scope.type == "ParallelCoordinatePlot"){
        element.style("fill", "none");
        element.style("stroke", color);
        element.style("stroke-width", scope.defaultWidth);
        element.style('opacity', scope.defaultOpacity);
      }

      else {
        element.style("fill", color);
        element.style('opacity', 1.0);
      }
    }
    scope.temp_selection = [];
    scope.selectedElements = [];
  }

  // █   █ █████  ████ █   █ █     █████  ████ █   █ █████ 
  // █   █   █   █     █   █ █       █   █     █   █   █   
  // █████   █   █  ██ █████ █       █   █  ██ █████   █   
  // █   █   █   █   █ █   █ █       █   █   █ █   █   █   
  // █   █ █████  ███  █   █ █████ █████  ███  █   █   █   

  // Highlight event
  highlightElement(event, callBack) {
    let scope = this;

    if (scope.highlightElementFlagCheck) {
      scope.clearHighlights();

      let highlightedElement;
      let group = event.target;

      if (highlightedElement = d3.select(group)) {
        scope.highlightGroup(highlightedElement, event, callBack, group);
      }
    }
  }

  highlightGroup(highlightedElement, event, callBack,){
    let scope = this;

    if (highlightedElement.attr('data-id') != undefined) {
      scope.applyHighlightColor(highlightedElement);

      scope.applyToolTip(event, highlightedElement.attr('data-id'));
      callBack(scope.data[highlightedElement.attr("data-id")].ids);
    }

    else {
      callBack("");
      plotMgrInstance.deHighlightElementsInAllPlots();
    }
  }

  //Highlight Element
  highlightElements(elementIDs) {
    let scope = this;

    if (scope.highlightElementFlagCheck) {
      if (scope.svg != undefined) {
        if (elementIDs) {
          let elems = {};
          elementIDs.forEach(id => {

            let key = scope.elements[id];

            if (!elems[key]) elems[key] = [];
            elems[key].push(id);
          });

         scope.elementGroup(elems);
        }
      }
    }
  }

  elementGroup(elems){
    let scope = this;

    Object.keys(elems).forEach(key => {
      var element = scope.getElementToHighlight(key)
      if (scope.data[key]) {
        if (scope.data[key].ids.length === elems[key].length) {
          scope.applyHighlightColor(element);
        }
        else {
          scope.applyPartialHighlightColor(element);
        }
      }
    });

  }

  getElementToHighlight(key){
    let scope = this;
    return d3.select(scope.groups[key]);
  }

  // Clear highlight selection 
  clearHighlights() {
    let scope = this;

    for (let i = 0; i < scope.temp_highlight.length; i++) {
      let element = scope.temp_highlight[i];

      var color = scope.defaultColor;
      var opacity = 1.0;
      var width = scope.defaultWidth;
      if (element.attr('data-isClr') == 'true') color = element.attr('data-color');
      if (element.attr('data-isSel') == 'true') color = scope.selectionColor;
      if (element.attr('data-isPar') == 'true') {
          color = scope.selectionColor;
          opacity = scope.selectionOpacity;
      }

      if (scope.type == "ParallelCoordinatePlot"){
        element.style("fill", "none");
        element.style("stroke", color);
        element.style("stroke-width", width);
        element.style('opacity', opacity);
      }

      else {
        element.style("fill", color);
        element.style('opacity', opacity);
      }

    }
    scope.temp_highlight = [];
  }

  // apply selection color
  applyHighlightColor(element) {
    let scope = this;

    if (scope.type == "ParallelCoordinatePlot"){
      scope.temp_highlight.push(element)
      element.style("fill", "none");
      element.style("stroke", scope.highlightColor);
      element.style("stroke-width", scope.highlightWidth);
      element.style('opacity', scope.baseOpacity);
      scope.raiseElements(element);
    }

    else{
      scope.temp_highlight.push(element)
      element.style("fill", scope.highlightColor);
      element.style('opacity', 1.0);
    }
  }

  // apply partial selection color
  applyPartialHighlightColor(element) {
    let scope = this;

    if (scope.type == "ParallelCoordinatePlot"){
      scope.temp_highlight.push(element)
      element.style("fill", "none");
      element.style("stroke", scope.highlightColor);
      element.style("stroke-width", scope.defaultWidth);
      element.style('opacity', 0.5);
    }

    else {
      scope.temp_highlight.push(element)
      element.style("fill", scope.highlightColor);
      element.style('opacity', 0.5);
    }
  }

  // apply highlight color API
  applyHighlightTransparency(element) {
    let scope = this;

    scope.temp_highlight.push(element)
    element.style("opacity", scope.highlightOpacity);
  }

  // apply tool tip
  applyToolTip(event, id) {
    let scope = this;
    let canvasHeight = scope.canvas.offsetHeight
    let canvasWidth = scope.canvas.offsetWidth

    utils.formatTooltipMove(d3.pointer(event), scope.tooltip, canvasWidth, canvasHeight);
    
    let content = scope.buildTooltipContent(id);
    if (scope.keys.y != undefined) {
      content += "<strong>" + scope.keys.y + "</strong> | " + utils.setDigits(scope.data[id].value, scope.digits);
    }
    scope.tooltip.html(content);
  }


}

export {Plot}